import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../components/shared';
import { AnnounceBar } from '../components/shared/AnnounceBar';
import { SiteNav, PAGE_LINKS } from '../components/shared/SiteNav';

interface TeamMember {
  name: string;
  role: string;
  credentials: string[];
  photo?: string;
}

const foundingTeam: TeamMember[] = [
  {
    name: "Aansh Shah",
    role: "Founder, Managing Partner",
    credentials: [
      "Built and scaled ML observability platforms at Amazon (Alexa, AGI)",
      "Brown University, Applied Math & CS",
      "Founded Briefcase AI to solve the AI governance gap in regulated financial services"
    ],
    photo: "/assets/images/team/aansh.png"
  },
  {
    name: "Brett Taxin",
    role: "Founder, Managing Partner",
    credentials: [
      "Two decades scaling regulated financial platforms across fintech and community banking",
      "Experience at the intersection of policy and financial services — U.S. Treasury and the White House",
      "University of Pennsylvania (BA); University of Chicago (JD/MBA)"
    ],
    photo: "/assets/images/team/brett-taxin.png"
  }
];

function TeamMemberCard({ member }: { member: TeamMember }) {
  return (
    <article className="bc-panel p-6 sm:p-8">
      <div className="flex items-center gap-4">
        {member.photo && (
          <img
            src={member.photo}
            alt=""
            className="h-14 w-14 shrink-0 rounded-md object-cover"
          />
        )}
        <div className="min-w-0">
          <h3 className="bc-h3">{member.name}</h3>
          <p className="mt-1 text-[14px] tracking-[0.0175em] text-ink-3">{member.role}</p>
        </div>
      </div>

      <ul className="mt-6 space-y-3 border-t border-rule pt-6">
        {member.credentials.map((credential) => (
          <li key={credential} className="text-[15px] leading-[1.7] text-ink-2">
            {credential}
          </li>
        ))}
      </ul>
    </article>
  );
}

export const TeamPage: React.FC = () => {
  const canonicalUrl = 'https://briefcaseai.org/team';
  const socialImage = 'https://briefcaseai.org/briefcase-ai-thumbnail.png';
  const seoDescription =
    'Meet the team building Briefcase AI, managed infrastructure for AI agents that can act with control and accountability.';

  return (
    <>
      <Helmet>
        <title>About Briefcase AI | Founding Team</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content="briefcase ai team, ai compliance infrastructure, regulated ai, ai governance platform, explainable ai operations" />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />

        {/* Open Graph */}
        <meta property="og:title" content="About Briefcase AI | Founding Team" />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={socialImage} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Briefcase AI | Founding Team" />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={socialImage} />

        {/* Structured Data - Organization Team */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Briefcase AI",
            "url": "https://briefcaseai.org",
            "description": "Managed agent platform for regulated operations",
            "founder": [
              {
                "@type": "Person",
                "name": "Aansh Shah",
                "jobTitle": "Founder, Chief Executive Officer",
                "alumniOf": "Brown University",
                "worksFor": "Briefcase AI"
              },
              {
                "@type": "Person",
                "name": "Brett Taxin",
                "jobTitle": "Founder, Managing Partner Revenue and Strategy",
                "alumniOf": ["University of Pennsylvania", "University of Chicago"],
                "worksFor": "Briefcase AI"
              }
            ]
          })}
        </script>
      </Helmet>

      <AnnounceBar />

      <div className="bc-shell">
        <SiteNav links={PAGE_LINKS} />

        <main className="px-5 pb-24 pt-10 sm:px-8 sm:pt-16">
          <div className="mx-auto max-w-[1120px]">
            <h1 className="bc-h2 max-w-[16ch]">Built by people who understand infrastructure and regulated operations.</h1>
            <p className="bc-prose mt-6 max-w-[62ch]">
              We combine AI infrastructure, financial-services operations, and policy experience to
              build agents that can do useful work without making accountability an afterthought.
            </p>
          </div>
        </main>

        <section className="border-t border-rule bg-paper-alt px-5 py-[72px] sm:px-8 lg:py-[120px]">
          <div className="mx-auto max-w-[1120px]">
            <h2 className="bc-h2">The people behind Briefcase AI</h2>
            <p className="bc-prose mt-6 max-w-[68ch]">
              We started Briefcase AI to give teams a practical way to put agents into real
              workflows while keeping authority, evidence, and review connected.
            </p>

            <div className="mt-14 grid gap-5 lg:grid-cols-2">
              {foundingTeam.map((member) => (
                <TeamMemberCard key={member.name} member={member} />
              ))}
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};
